import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material'
import {
    fetchConsentDetails,
    readAuthorizeParams,
    submitConsent,
    type ConsentAssociation,
    type ConsentDetails,
} from '#app/api/consent'
import { displayName, fetchSession, logout, type SessionInfo } from '#app/api/session'
import AuthLayout from '#components/AuthLayout'

// Maps the backend's grant-TTL codes to translation keys.
const TTL_KEYS: Record<string, string> = {
    '30m': 'ttl30m',
    '8h': 'ttl8h',
    '30d': 'ttl30d',
}

// A user with a handful of organisations can tick them one by one; beyond
// this the list needs a select-all toggle and its own scroll area so the
// Allow/Deny buttons stay reachable.
const LONG_LIST_THRESHOLD = 6

// The association exists but the session's login cannot activate it — the
// backend then supplies login links (session augmentation) that return here.
// Older fixrate-security versions omit the fields; treat those as activatable.
function needsAdditionalLogin(assoc: ConsentAssociation): boolean {
    return assoc.activatable === false && (assoc.missingIdps?.length ?? 0) > 0
}

export default function ConsentPage() {
    const { t, i18n } = useTranslation()
    const [searchParams] = useSearchParams()
    const params = useMemo(() => readAuthorizeParams(searchParams), [searchParams])

    const [details, setDetails] = useState<ConsentDetails | null>(null)
    const [session, setSession] = useState<SessionInfo | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [selectedOrgs, setSelectedOrgs] = useState<Set<string>>(new Set())
    const [ttl, setTtl] = useState<string>('30m')
    const [acknowledged, setAcknowledged] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        let cancelled = false
        Promise.all([fetchConsentDetails(params), fetchSession()])
            .then(([data, sessionInfo]) => {
                if (cancelled) return
                setDetails(data)
                setSession(sessionInfo)
                const allOrgs = new Set(
                    data.associations.filter((a) => a.selectable).map((a) => a.organisationId)
                )
                setSelectedOrgs(allOrgs)
                if (data.supported_grant_ttls.length > 0) setTtl(data.supported_grant_ttls[0]!)
            })
            .catch((err: Error) => {
                if (!cancelled) setError(err.message)
            })
        return () => {
            cancelled = true
        }
    }, [params])

    // Only selectable associations can be ticked, so they alone decide what
    // "all" means for the select-all toggle.
    const selectableIds = useMemo(
        () =>
            (details?.associations ?? [])
                .filter((a) => a.selectable)
                .map((a) => a.organisationId),
        [details]
    )
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedOrgs.has(id))
    const someSelected = selectedOrgs.size > 0 && !allSelected

    // Actionable rows first, then alphabetically. The backend returns
    // associations in session order, which is arbitrary to the user.
    // Sort in the active language so æ/ø/å and ä/ö land where they should.
    const sortedAssociations = useMemo(() => {
        const name = (a: ConsentAssociation) => a.organisationName || a.organisationId
        return [...(details?.associations ?? [])].sort(
            (a, b) =>
                Number(b.selectable) - Number(a.selectable) ||
                name(a).localeCompare(name(b), i18n.language)
        )
    }, [details, i18n.language])

    const toggleOrg = (id: string) => {
        setSelectedOrgs((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const toggleAll = () => {
        setSelectedOrgs(allSelected ? new Set() : new Set(selectableIds))
    }

    const onSwitchUser = async () => {
        setSubmitting(true)
        try {
            await logout()
        } catch {
            // ignore — restarting the flow re-evaluates the session either way
        }
        // Restart the OAuth flow rather than reloading /consent — without a
        // session, /consent returns 401, so we send the user back through
        // /oauth/authorize which 303s to /login with originalUrl preserved.
        const qs = new URLSearchParams(window.location.search)
        window.location.assign(`/oauth/authorize?${qs.toString()}`)
    }

    const finalise = async (approved: boolean) => {
        setSubmitting(true)
        try {
            const result = await submitConsent({
                params,
                approved,
                grantTtl: approved ? ttl : undefined,
                allowedOrganisationIds: approved ? Array.from(selectedOrgs) : undefined,
            })
            window.location.assign(result.redirect_url)
        } catch (err) {
            setSubmitting(false)
            setError(err instanceof Error ? err.message : String(err))
        }
    }

    if (error) {
        return (
            <AuthLayout>
                <Alert severity="error">
                    <AlertTitle>{t('errorTitle')}</AlertTitle>
                    {error}
                </Alert>
            </AuthLayout>
        )
    }

    if (!details) {
        return (
            <AuthLayout>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            </AuthLayout>
        )
    }

    const clientName = details.client.client_name || details.client.client_id
    const hasAssociations = details.associations.length > 0
    // Rows that need an extra login get their own inline login link; the
    // generic contact-support hint is for the remaining unselectable rows.
    const hasUnselectableAssociation = details.associations.some((a) => !a.selectable && !needsAdditionalLogin(a))
    const isLongList = details.associations.length > LONG_LIST_THRESHOLD
    const hasNoSelectableAssociation = selectableIds.length === 0

    // Resource-tailored copy: prefer `consent.<resource>.*`, fall back to
    // `consent.default.*`. Description and note are optional (empty → hidden).
    const resource = details.resource
    const headingText = t([`consent.${resource}.heading`, 'consent.default.heading'], { client: clientName })
    const descriptionKey = [`consent.${resource}.description`, 'consent.default.description']
    const descriptionText = t(descriptionKey, { client: clientName, resource })
    // The note reacts to the selected duration; use the human-readable TTL
    // label (e.g. "30 minutter") rather than the raw "30m" code.
    const durationLabel = TTL_KEYS[ttl] ? t(TTL_KEYS[ttl]) : ttl
    const noteText = t([`consent.${resource}.note`, 'consent.default.note'], {
        client: clientName,
        duration: durationLabel,
    })
    const disclaimerText = t([`consent.${resource}.disclaimer`, 'consent.default.disclaimer'], {
        client: clientName,
    })
    const acknowledgementText = t(
        [`consent.${resource}.acknowledgement`, 'consent.default.acknowledgement'],
        { client: clientName }
    )
    const canSubmit = selectedOrgs.size > 0 && !submitting && (!acknowledgementText || acknowledged)

    return (
        <AuthLayout maxWidth={520}>
                <Stack spacing={3}>
                    {session?.authenticated && (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                                <Trans i18nKey="signedInAs" values={{ name: displayName(session) }} components={[<b key="name" />]} />
                            </Typography>
                            <Button size="small" variant="text" onClick={onSwitchUser} disabled={submitting}>
                                {t('switchUser')}
                            </Button>
                        </Stack>
                    )}
                    <Box>
                        <Typography variant="h1" component="h1" gutterBottom>
                            {hasAssociations ? headingText : t('noAssociationsTitle')}
                        </Typography>
                        {hasAssociations && descriptionText && (
                            <Typography variant="body1" color="text.secondary">
                                <Trans
                                    i18nKey={descriptionKey}
                                    values={{ client: clientName, resource }}
                                    components={[<b key="resource" />]}
                                />
                            </Typography>
                        )}
                        {hasAssociations && details.client.client_uri && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <Link href={details.client.client_uri} target="_blank" rel="noreferrer noopener">
                                    {details.client.client_uri}
                                </Link>
                            </Typography>
                        )}
                    </Box>

                    {!hasAssociations ? (
                        // Wrong-account case: the signed-in user has no organisations
                        // that can grant this audience. Offer switching accounts
                        // rather than a dead-end disabled Allow button.
                        <>
                            <Alert severity="warning">{t('noAssociationsBody')}</Alert>
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button variant="contained" onClick={onSwitchUser} disabled={submitting}>
                                    {t('switchAccount')}
                                </Button>
                            </Stack>
                        </>
                    ) : (
                        <>
                            <FormControl fullWidth>
                                <FormLabel sx={{ mb: 1 }}>{t('accessDuration')}</FormLabel>
                                <Select size="small" value={ttl} onChange={(e) => setTtl(e.target.value)}>
                                    {details.supported_grant_ttls.map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {TTL_KEYS[value] ? t(TTL_KEYS[value]) : value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box>
                                <FormLabel sx={{ mb: 1, display: 'block' }}>{t('organisationsLabel')}</FormLabel>
                                {/* Outside the scroll area below, so it stays put on a long list. */}
                                {isLongList && selectableIds.length > 0 && (
                                    <>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={allSelected}
                                                    indeterminate={someSelected}
                                                    onChange={toggleAll}
                                                />
                                            }
                                            label={t('selectAll')}
                                        />
                                        <Divider sx={{ mb: 1 }} />
                                    </>
                                )}
                                <Stack
                                    sx={
                                        isLongList
                                            ? { maxHeight: 320, overflowY: 'auto', pr: 1 }
                                            : undefined
                                    }
                                >
                                    {sortedAssociations.map((assoc) => (
                                        <Box key={assoc.organisationId}>
                                            <FormControlLabel
                                                // Also on the label, not just the control: MUI only
                                                // dims the organisation name when the wrapper knows
                                                // it is disabled, and a black name next to a greyed
                                                // box reads as "tickable".
                                                disabled={!assoc.selectable}
                                                control={
                                                    <Checkbox
                                                        checked={selectedOrgs.has(assoc.organisationId)}
                                                        disabled={!assoc.selectable}
                                                        onChange={() => toggleOrg(assoc.organisationId)}
                                                    />
                                                }
                                                // A plain string, not a Typography node: MUI only
                                                // wraps string labels in its own .MuiFormControlLabel-label,
                                                // which is what carries the disabled colour.
                                                label={assoc.organisationName || assoc.organisationId}
                                            />
                                            {needsAdditionalLogin(assoc) && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', ml: 4, mt: -0.75, mb: 0.5 }}
                                                >
                                                    {assoc.missingIdps!.map((idp, index) => (
                                                        <span key={idp.href}>
                                                            {index > 0 && ' · '}
                                                            <Link href={idp.href}>
                                                                {t('loginToInclude', { idp: idp.name })}
                                                            </Link>
                                                        </span>
                                                    ))}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                                {/* Nothing selectable at all is a dead end, not a footnote —
                                    the form stays visible so the user can see which
                                    organisations exist, but the reason gets an alert. */}
                                {hasNoSelectableAssociation ? (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                        {t('noSelectableBody', { client: clientName })}
                                    </Alert>
                                ) : (
                                    hasUnselectableAssociation && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mt: 1 }}
                                        >
                                            {t('unselectableHint', { client: clientName })}
                                        </Typography>
                                    )
                                )}
                            </Box>

                            {noteText && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {noteText}
                                </Typography>
                            )}

                            {(disclaimerText || acknowledgementText) && (
                                <Box>
                                    {disclaimerText && (
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {disclaimerText}
                                        </Typography>
                                    )}
                                    {acknowledgementText && (
                                        <FormControlLabel
                                            // The label runs to several lines, so the box aligns
                                            // with the first one instead of the middle of the block.
                                            sx={{ alignItems: 'flex-start', mr: 0 }}
                                            control={
                                                <Checkbox
                                                    sx={{ pt: 0.25 }}
                                                    checked={acknowledged}
                                                    onChange={(e) => setAcknowledged(e.target.checked)}
                                                />
                                            }
                                            label={<Typography variant="body2">{acknowledgementText}</Typography>}
                                        />
                                    )}
                                </Box>
                            )}

                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button onClick={() => finalise(false)} disabled={submitting} color="inherit">
                                    {t('deny')}
                                </Button>
                                {/* With nothing to approve, switching account is the only
                                    way forward the user has without calling support. */}
                                {hasNoSelectableAssociation ? (
                                    <Button variant="contained" onClick={onSwitchUser} disabled={submitting}>
                                        {t('switchAccount')}
                                    </Button>
                                ) : (
                                    <Button onClick={() => finalise(true)} disabled={!canSubmit} variant="contained">
                                        {t('allow')}
                                    </Button>
                                )}
                            </Stack>
                        </>
                    )}
                </Stack>
        </AuthLayout>
    )
}
